import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, ShieldCheck, Search } from "lucide-react";
import { API_URL } from "../data/site";
import { Reveal } from "./Reveal";

const inputClass =
  "w-full border border-neutral-300 bg-white px-4 py-3 text-sm text-[#111111] outline-none transition-colors duration-300 placeholder:text-neutral-400 focus:border-[#111111]";
const labelClass = "font-overline text-[10px] uppercase tracking-[0.25em] text-neutral-500";
const buttonClass =
  "group inline-flex w-full items-center justify-center gap-2 bg-[#111111] px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-[#e63946] disabled:cursor-not-allowed disabled:opacity-60";

const PAYMENTS_ENABLED = process.env.REACT_APP_PAYMENTS_ENABLED === "true";

const money = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const errorText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

export const ServicePayments = () => {
  const [mode, setMode] = useState("lookup");
  const [reference, setReference] = useState("");
  const [email, setEmail] = useState("");
  const [quote, setQuote] = useState(null);
  const [option, setOption] = useState("total");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const findQuote = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/quotes/lookup`, { reference: reference.trim(), email: email.trim() });
      setQuote(res.data);
      setOption(res.data.status === "deposit_paid" || !res.data.deposit ? "total" : "deposit");
    } catch (err) {
      toast.error(errorText(err, "We couldn't find that quote right now. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const checkout = async (payload) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/payments/quoted-checkout`, { ...payload, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(errorText(err, "Checkout is unavailable right now. Please use the inquiry form instead."));
      setBusy(false);
    }
  };

  const payQuote = (e) => {
    e.preventDefault();
    checkout({ reference: quote.reference, email: email.trim(), option });
  };

  const payCustom = (e) => {
    e.preventDefault();
    const value = Number(amount);
    if (!Number.isFinite(value) || value < 1) {
      toast.error("Enter the amount from your quote (minimum $1).");
      return;
    }
    checkout({ amount: Math.round(value * 100) / 100, reference: reference.trim() });
  };

  const switchMode = (next) => {
    setMode(next);
    setQuote(null);
  };

  return (
    <section data-testid="payments-section" className="border-t border-neutral-200 bg-[#f8f9fa]">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 py-20 md:grid-cols-12 md:px-8 md:py-28">
        <div className={PAYMENTS_ENABLED ? "md:col-span-6" : "md:col-span-8"}>
          <Reveal>
            <p className="font-overline text-[10px] uppercase tracking-[0.35em] text-[#e63946] md:text-[11px]">Build your program</p>
            <h2 className="font-display mt-5 text-3xl font-extrabold tracking-tight text-[#111111] sm:text-4xl lg:text-5xl">
              Every program is
              <br />
              built to the driver.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-600">
              No two seasons look alike, so we scope each engagement on a call and send you a written quote. Start the
              conversation and we'll shape the program around your calendar, your car and your goals.
            </p>
            <Link
              to="/contact"
              data-testid="payments-contact-link"
              className="group mt-8 inline-flex items-center gap-3 bg-[#111111] px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-[#e63946]"
            >
              Contact us to discuss your program
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>

        {PAYMENTS_ENABLED && (
        <div className="md:col-span-6 md:pl-8">
          <Reveal delay={0.1}>
            <div data-testid="quoted-payment-panel" className="border border-neutral-200 bg-white p-8 md:p-10">
              <p className={`${labelClass} tracking-[0.3em]`}>Already quoted?</p>
              <h3 className="font-display mt-3 text-xl font-bold tracking-tight text-[#111111]">Pay your quote online.</h3>

              {mode === "lookup" && !quote && (
                <form onSubmit={findQuote} data-testid="quote-lookup-form" className="mt-6">
                  <p className="text-sm leading-relaxed text-neutral-600">
                    Enter the reference from your quote and the email we sent it to.
                  </p>
                  <div className="mt-6 grid gap-5">
                    <label className="block">
                      <span className={labelClass}>Quote reference</span>
                      <input type="text" required maxLength={40} value={reference} onChange={(e) => setReference(e.target.value.toUpperCase())} placeholder="ABS-2026-XXXXXX" data-testid="quote-reference-input" className={`${inputClass} mt-2 uppercase`} />
                    </label>
                    <label className="block">
                      <span className={labelClass}>Email on the quote</span>
                      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="quote-email-input" className={`${inputClass} mt-2`} />
                    </label>
                  </div>
                  <button type="submit" data-testid="quote-lookup-button" disabled={busy} className={`${buttonClass} mt-8`}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4" />Find my quote</>}
                  </button>
                  <button type="button" data-testid="switch-to-custom-amount" onClick={() => switchMode("custom")} className="mt-4 text-xs text-neutral-500 underline decoration-neutral-300 underline-offset-4 transition-colors duration-300 hover:text-[#e63946]">
                    Quoted by phone or email without a reference? Enter your amount instead.
                  </button>
                </form>
              )}

              {quote && (
                <form onSubmit={payQuote} data-testid="quote-pay-form" className="mt-6">
                  <div data-testid="quote-summary" className="border border-neutral-200 bg-[#f8f9fa] p-5">
                    <p className="font-overline text-[10px] tracking-[0.25em] text-[#e63946]">{quote.reference}</p>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-700">{quote.description}</p>
                  </div>
                  <fieldset className="mt-6 grid gap-3">
                    <legend className={labelClass}>Choose what to pay</legend>
                    {quote.deposit && quote.status !== "deposit_paid" && (
                      <label data-testid="quote-option-deposit" className={`flex cursor-pointer items-center justify-between border px-5 py-4 transition-colors duration-300 ${option === "deposit" ? "border-[#111111]" : "border-neutral-300"}`}>
                        <span className="flex items-center gap-3 text-sm text-[#111111]">
                          <input type="radio" name="option" value="deposit" checked={option === "deposit"} onChange={() => setOption("deposit")} className="accent-[#e63946]" />
                          Deposit to reserve
                        </span>
                        <span className="font-display text-lg font-bold text-[#111111]">{money(quote.deposit)}</span>
                      </label>
                    )}
                    <label data-testid="quote-option-total" className={`flex cursor-pointer items-center justify-between border px-5 py-4 transition-colors duration-300 ${option === "total" ? "border-[#111111]" : "border-neutral-300"}`}>
                      <span className="flex items-center gap-3 text-sm text-[#111111]">
                        <input type="radio" name="option" value="total" checked={option === "total"} onChange={() => setOption("total")} className="accent-[#e63946]" />
                        {quote.status === "deposit_paid" ? "Balance — full program total" : "Full program total"}
                      </span>
                      <span className="font-display text-lg font-bold text-[#111111]">{money(quote.total)}</span>
                    </label>
                  </fieldset>
                  {quote.status === "paid" ? (
                    <p data-testid="quote-paid-notice" className="mt-6 text-sm text-neutral-600">This quote has been paid in full. Thank you.</p>
                  ) : (
                    <button type="submit" data-testid="quoted-pay-button" disabled={busy} className={`${buttonClass} mt-8`}>
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue to secure checkout<ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>}
                    </button>
                  )}
                  <button type="button" data-testid="quote-reset-button" onClick={() => setQuote(null)} className="mt-4 text-xs text-neutral-500 underline decoration-neutral-300 underline-offset-4 transition-colors duration-300 hover:text-[#e63946]">
                    Look up a different quote
                  </button>
                </form>
              )}

              {mode === "custom" && (
                <form onSubmit={payCustom} data-testid="custom-amount-form" className="mt-6">
                  <p className="text-sm leading-relaxed text-neutral-600">Enter the amount from your quote and any reference we gave you.</p>
                  <div className="mt-6 grid gap-5">
                    <label className="block">
                      <span className={labelClass}>Amount (USD)</span>
                      <div className="relative mt-2">
                        <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-sm text-neutral-500">$</span>
                        <input type="number" inputMode="decimal" min="1" max="50000" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="quoted-amount-input" className={`${inputClass} pl-8`} />
                      </div>
                    </label>
                    <label className="block">
                      <span className={labelClass}>Reference <span className="normal-case tracking-normal text-neutral-400">(optional)</span></span>
                      <input type="text" maxLength={40} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Invoice or quote note" data-testid="custom-reference-input" className={`${inputClass} mt-2`} />
                    </label>
                  </div>
                  <button type="submit" data-testid="custom-pay-button" disabled={busy} className={`${buttonClass} mt-8`}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue to secure checkout<ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>}
                  </button>
                  <button type="button" data-testid="switch-to-lookup" onClick={() => switchMode("lookup")} className="mt-4 text-xs text-neutral-500 underline decoration-neutral-300 underline-offset-4 transition-colors duration-300 hover:text-[#e63946]">
                    Have a quote reference? Look it up instead.
                  </button>
                </form>
              )}

              <p className="mt-6 flex items-center gap-2 text-xs text-neutral-500">
                <ShieldCheck className="h-4 w-4 shrink-0 text-[#e63946]" />
                Processed by Stripe. Card details never touch this website.
              </p>
            </div>
          </Reveal>
        </div>
        )}
      </div>
    </section>
  );
};
